import React from 'react';
import PropTypes from 'prop-types';

import { toast } from '@meronex/components';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { useTheme } from '@material-ui/core/styles';

import ILifeBuoy from '@meronex/icons/fi/FiLifeBuoy';

import TopHeader from '../../../views/components/TopHeader';
import { siteSettingsMng } from '../api/siteSettingsMng';
import { setSupportContact } from '../../../../config/support';

// Lets an administrator set the "who do I contact?" details operators see when
// something is switched off or they lack permission. Saved server-side, so it
// takes effect for everyone without a redeploy.
function SupportContactDialog(props) {
  const { open, onClose } = props;
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [contact, setContact] = React.useState({ name: '', email: '', phone: '' });

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    siteSettingsMng
      .getSiteSettings()
      .then((settings) => {
        if (cancelled) return;
        const saved = (settings && settings.supportContact) || {};
        setContact({
          name: saved.name || '',
          email: saved.email || '',
          phone: saved.phone || '',
        });
      })
      .catch((e) => {
        console.error('Could not load support contact', e);
        if (!cancelled) toast.error('Could not load the current support contact');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await siteSettingsMng.updateSiteSettings({ supportContact: contact });
      // Reflect it immediately for this session too.
      setSupportContact(contact);
      toast.success('Support contact saved', { autoClose: 900 });
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(
        e && e.reason ? `Could not save: ${e.reason}` : 'Could not save the support contact'
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, placeholder) => (
    <TextField
      label={label}
      value={contact[key]}
      placeholder={placeholder}
      onChange={(e) => setContact({ ...contact, [key]: e.target.value })}
      variant="outlined"
      fullWidth
      margin="normal"
      disabled={loading || saving}
      InputLabelProps={{ shrink: true }}
    />
  );

  if (!open) return null;

  return (
    <Dialog fullWidth maxWidth="xs" className={'fullScreenMobile'} open={open} onClose={onClose}>
      <DialogTitle>
        <TopHeader title={'Support Contact'} onClose={onClose} icon={<ILifeBuoy size={20} />} />
      </DialogTitle>
      <DialogContent>
        <div style={{ fontSize: '12.5px', lineHeight: 1.5, color: isDark ? '#94a3b8' : '#64748b' }}>
          Shown to staff when something is switched off or they don't have
          permission, so they know who to ask. Leave the name empty to show a
          generic "contact your administrator" message instead.
        </div>
        {field('Name', 'name', 'e.g. Meronex Support')}
        {field('Email', 'email', 'e.g. ali@meronex.com')}
        {field('Phone (optional)', 'phone', 'e.g. +971 50 000 0000')}
        <div style={{ textAlign: 'right', margin: '18px 0 10px' }}>
          <span
            onClick={() => !saving && onClose()}
            style={{
              cursor: saving ? 'default' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: isDark ? '#94a3b8' : '#64748b',
              padding: '8px 14px',
            }}>
            Cancel
          </span>
          <span
            onClick={() => !saving && !loading && save()}
            style={{
              cursor: saving || loading ? 'default' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: saving || loading ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#818cf8' : '#6366f1'),
              padding: '8px 4px',
            }}>
            {saving ? 'Saving...' : 'Save'}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

SupportContactDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SupportContactDialog;
